/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useEffect, useLayoutEffect, useRef, useState, useCallback, useMemo} from 'react'
import {createPortal} from 'react-dom'
import PropTypes from 'prop-types'
import {MenuContent} from './partials/MenuContent'
import {Box, Text, useMultiStyleConfig, Button} from '@chakra-ui/react'
import {getGlobalLink} from '../../utils/contentstack-utils'
import {useLocation} from 'react-router-dom'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'

const useClientLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect

const ListMenuPopover = ({
    primarycategory,
    categoriesData,
    selectedMenuItem,
    setSelectedMenuItem,
    bandElement,
    onHeightChange,
    onVisibilityChange,
}) => {
    const styles = useMultiStyleConfig('ListMenu')
    const location = useLocation()
    const {locale} = useMultiSite()
    const isOpen = selectedMenuItem === primarycategory.uid
    const [isPresent, setIsPresent] = useState(isOpen)
    const paneRef = useRef(null)
    const previousLocation = useRef(location.pathname)

    const {label} = useMemo(
        () =>
            getGlobalLink({
                link: primarycategory?.primary_category?.primary_category_redirect,
                categoriesData,
                locale: locale?.alias,
            }),
        [primarycategory, categoriesData, locale?.alias],
    )

    const colorOverride = primarycategory?.primary_category?.override_color
    const isVisible = isOpen || isPresent

    const categoryID = primarycategory?.redirects?.categorysfcc.data?.[0]?.id ?? primarycategory.uid

    const menuContainerRef = useRef(null)
    const handleBlur = useCallback(
        (event) => {
            if (
                !menuContainerRef.current?.contains(event.relatedTarget) &&
                !paneRef.current?.contains(event.relatedTarget)
            ) {
                setSelectedMenuItem(null)
            }
        },
        [setSelectedMenuItem],
    )

    const handleTriggerClick = useCallback(() => {
        setSelectedMenuItem(isOpen ? null : primarycategory.uid)
    }, [isOpen, primarycategory.uid, setSelectedMenuItem])

    useEffect(() => {
        if (previousLocation.current !== location.pathname) {
            setSelectedMenuItem(null)
        }
        previousLocation.current = location.pathname
    }, [location.pathname, setSelectedMenuItem])

    useEffect(() => {
        if (isOpen) {
            setIsPresent(true)
            onVisibilityChange(primarycategory.uid, true)
            return
        }
        if (!isPresent) return

        const timeout = setTimeout(() => {
            setIsPresent(false)
            onVisibilityChange(primarycategory.uid, false)
        }, 260)
        return () => clearTimeout(timeout)
    }, [isOpen, isPresent, onVisibilityChange, primarycategory.uid])

    useEffect(
        () => () => onVisibilityChange(primarycategory.uid, false),
        [onVisibilityChange, primarycategory.uid],
    )

    useClientLayoutEffect(() => {
        if (!isOpen || !paneRef.current) return

        const pane = paneRef.current
        const measure = () => onHeightChange(primarycategory.uid, pane.offsetHeight)
        measure()
        const observer = new ResizeObserver(measure)
        observer.observe(pane)
        return () => observer.disconnect()
    }, [isOpen, bandElement, onHeightChange, primarycategory.uid])

    return (
        <Box ref={menuContainerRef} onBlur={handleBlur}>
            <Box {...styles.listMenuTriggerContainer}>
                <Button
                    variant="ghost"
                    size="base"
                    onClick={handleTriggerClick}
                    aria-expanded={isOpen}
                    aria-haspopup="true"
                    aria-controls={`menu-${categoryID}`}
                    aria-label={label}
                    id={`heading-${categoryID}`}
                >
                    <Text
                        {...(isOpen ? styles.listMenuTriggerLinkActive : {})}
                        {...{name: label + ' __'}}
                        aria-hidden="true"
                        color={
                            colorOverride && colorOverride !== ''
                                ? colorOverride
                                : 'foreground.neutral.default'
                        }
                    >
                        {label}
                    </Text>
                </Button>
            </Box>
            {bandElement &&
                createPortal(
                    <Box
                        ref={paneRef}
                        id={`menu-${categoryID}`}
                        data-testid={categoryID}
                        className={isVisible ? 'visible-menu' : 'hidden-menu'}
                        position={isOpen ? 'relative' : 'absolute'}
                        top={0}
                        left={0}
                        width="100%"
                        zIndex={isOpen ? 2 : 1}
                        visibility={isVisible ? 'visible' : 'hidden'}
                        pointerEvents={isOpen ? 'auto' : 'none'}
                        aria-hidden={!isOpen}
                        inert={isOpen ? undefined : ''}
                        opacity={isOpen ? 1 : 0}
                        transform={isOpen ? 'translateY(0)' : 'translateY(-40px)'}
                        transition={
                            isOpen
                                ? 'transform 320ms cubic-bezier(0.05, 0.7, 0.1, 1), opacity 320ms cubic-bezier(0.05, 0.7, 0.1, 1)'
                                : 'transform 160ms cubic-bezier(0.4, 0, 0.2, 1), opacity 160ms cubic-bezier(0.4, 0, 0.2, 1)'
                        }
                        onKeyDown={(event) => {
                            if (event.key === 'Escape') {
                                setSelectedMenuItem(null)
                                document.getElementById(`heading-${categoryID}`)?.focus()
                            }
                        }}
                    >
                        <Box {...styles.listMenuContent} background="transparent">
                            <Box width="100%" layerStyle="page">
                                <MenuContent
                                    primarycategory={primarycategory}
                                    categoriesData={categoriesData}
                                    locale={locale}
                                />
                            </Box>
                        </Box>
                    </Box>,
                    bandElement,
                )}
        </Box>
    )
}

ListMenuPopover.propTypes = {
    categoriesData: PropTypes.object,
    primarycategory: PropTypes.object,
    selectedMenuItem: PropTypes.string,
    setSelectedMenuItem: PropTypes.func,
    bandElement: PropTypes.object,
    onHeightChange: PropTypes.func.isRequired,
    onVisibilityChange: PropTypes.func.isRequired,
    onItemClick: PropTypes.func,
}

const MemoListMenuPopover = React.memo(ListMenuPopover)

export {MemoListMenuPopover as ListMenuPopover}
